// src/features/bom/pages/BomRecipeDetailPage.tsx
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/Button';
import {
    getRecipe, deactivateRecipe, explodeBom, BomRecipe,
} from '../services/bomApi';
import {
    ArrowLeft, Pencil, Trash2, Package, ChefHat, Clock, Layers,
    FlaskConical, DollarSign, Calculator, Beaker, AlertTriangle,
    CheckCircle2, Sparkles, Timer, ShoppingBag, ChevronDown, ChevronUp,
    TrendingUp, Info, Copy,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

export function BomRecipeDetailPage() {
    const { recipeId } = useParams<{ recipeId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showExplosion, setShowExplosion] = useState(false);
    const [explosionQty, setExplosionQty] = useState(1);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showAllIngredients, setShowAllIngredients] = useState(false);

    const { data: recipe, isLoading, isError, refetch } = useQuery({
        queryKey: ['bom-recipe', recipeId],
        queryFn: () => getRecipe(user!.tenantId, recipeId!),
        enabled: !!recipeId,
    });

    const { data: explosion } = useQuery({
        queryKey: ['bom-explosion', recipe?.finishedProductId, explosionQty],
        queryFn: () => explodeBom(user!.tenantId, recipe!.finishedProductId, explosionQty),
        enabled: !!recipe?.finishedProductId && showExplosion,
    });

    const deactivateMutation = useMutation({
        mutationFn: () => deactivateRecipe(user!.tenantId, recipeId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bom-recipes'] });
            addToast({ type: 'success', title: 'Recipe deactivated' });
            navigate('/bom');
        },
    });

    const formatNaira = (val: string | number) => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return `₦${(num || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Spinner size="xl" />
            </div>
        );
    }

    if (isError || !recipe) {
        return <ErrorState title="Recipe Not Found" onRetry={() => refetch()} />;
    }

    const wasteItems = recipe.items.filter(i => parseFloat(i.wasteFactor) > 1);
    const displayedIngredients = showAllIngredients ? recipe.items : recipe.items.slice(0, 8);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30">
            {/* Hero header */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700" />
                <div className="relative max-w-6xl mx-auto px-6 py-10">
                    <div className="flex items-center justify-between mb-6">
                        <button onClick={() => navigate('/bom')}
                            className="flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-xl text-white hover:bg-white/25 text-sm font-medium transition-all border border-white/10">
                            <ArrowLeft size={16} /> Back to Recipes
                        </button>
                        <div className="flex items-center gap-2">
                            <button onClick={() => navigate(`/bom/${recipeId}/edit`)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white/15 backdrop-blur-sm rounded-xl text-white hover:bg-white/25 text-sm font-medium transition-all border border-white/10">
                                <Pencil size={16} /> Edit
                            </button>
                            {recipe.isActive && (
                                <button onClick={() => setConfirmDelete(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white/15 backdrop-blur-sm rounded-xl text-white hover:bg-rose-500/60 text-sm font-medium transition-all border border-white/10">
                                    <Trash2 size={16} /> Deactivate
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-start gap-6">
                        <div className="p-5 bg-white/15 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/10">
                            <FlaskConical size={48} className="text-white" />
                        </div>
                        <div className="text-white flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h1 className="text-3xl md:text-4xl font-black tracking-tight">{recipe.name}</h1>
                                <Badge variant={recipe.isActive ? 'success' : 'danger'} size="md" className="backdrop-blur-sm bg-white/15 border border-white/20 text-white">
                                    {recipe.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                            </div>
                            <p className="text-white/70 text-base flex items-center gap-2">
                                <ShoppingBag size={16} />
                                Produces: <span className="font-semibold text-white">{recipe.finishedProductName || 'Unknown'}</span>
                            </p>
                            {recipe.description && (
                                <p className="text-white/60 text-sm mt-3 max-w-2xl">{recipe.description}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats row */}
            <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-10">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <QuickStat icon={Package} label="Ingredients" value={recipe.items.length} color="from-purple-500 to-indigo-600" delay={0} />
                    <QuickStat icon={Layers} label="Yield" value={`${recipe.yieldQuantity} ${recipe.yieldUomName || 'units'}`} color="from-blue-500 to-cyan-600" delay={0.1} />
                    <QuickStat icon={DollarSign} label="Cost per Yield" value={recipe.costPerYield ? formatNaira(recipe.costPerYield) : '—'} color="from-emerald-500 to-teal-600" delay={0.2} />
                    <QuickStat icon={TrendingUp} label="Est. Profit / Yield" value={recipe.finishedProductName && recipe.costPerYield ? formatNaira(parseFloat(recipe.finishedProductRetailPrice || '0') - recipe.costPerYield) : '—'} color="from-amber-500 to-orange-600" delay={0.3} />
                    <QuickStat icon={wasteItems.length > 0 ? AlertTriangle : CheckCircle2} label="Waste Items" value={wasteItems.length > 0 ? `${wasteItems.length}` : 'None'} color={wasteItems.length > 0 ? 'from-rose-500 to-red-600' : 'from-emerald-500 to-teal-600'} delay={0.4} />
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
                {recipe.preparationInstructions && (
                    <Card className="border-l-4 border-l-amber-400">
                        <CardHeader gradient className="bg-gradient-to-r from-amber-50 to-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-amber-100"><ChefHat className="w-5 h-5 text-amber-600" /></div>
                                <div><h3 className="font-semibold text-slate-800">How to Prepare</h3><p className="text-xs text-slate-500">Kitchen instructions</p></div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6">
                            <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{recipe.preparationInstructions}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Ingredients table */}
                <Card>
                    <CardHeader gradient className="bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-purple-100"><Beaker className="w-5 h-5 text-purple-600" /></div>
                            <div>
                                <h3 className="font-semibold text-slate-800">Ingredients</h3>
                                <p className="text-xs text-slate-500">{recipe.items.length} items needed per {recipe.yieldQuantity} {recipe.yieldUomName || 'servings'}</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/80">
                                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase w-10">#</th>
                                        <th className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase">Ingredient</th>
                                        <th className="px-5 py-3 text-right text-xs font-bold text-slate-500 uppercase">Qty</th>
                                        <th className="px-5 py-3 text-center text-xs font-bold text-slate-500 uppercase">Unit</th>
                                        <th className="px-5 py-3 text-center text-xs font-bold text-slate-500 uppercase">Waste</th>
                                        <th className="px-5 py-3 text-right text-xs font-bold text-slate-500 uppercase">Cost</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {displayedIngredients.map((item, idx) => {
                                        const wastePercent = parseFloat(item.wasteFactor) > 1
                                            ? ((parseFloat(item.wasteFactor) - 1) * 100).toFixed(0)
                                            : null;
                                        const itemCost = item.cost ? formatNaira(item.cost) : '—';
                                        return (
                                            <tr key={item.id} className="hover:bg-purple-50/20 transition-colors">
                                                <td className="px-5 py-3 text-xs text-slate-400 font-mono font-bold">{idx + 1}</td>
                                                <td className="px-5 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-7 h-7 rounded-md bg-purple-100 flex items-center justify-center">
                                                            <Package size={12} className="text-purple-500" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-semibold text-slate-800 truncate max-w-[250px]">
                                                                {item.rawMaterialName || item.rawMaterialSku || `Item ${idx + 1}`}
                                                            </p>
                                                            {item.rawMaterialSku && item.rawMaterialName && (
                                                                <p className="text-[10px] text-slate-400 font-mono">{item.rawMaterialSku}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-3 text-right text-sm font-bold text-slate-700 tabular-nums">
                                                    {parseFloat(item.quantity).toLocaleString()}
                                                </td>
                                                <td className="px-5 py-3 text-center">
                                                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md font-medium">
                                                        {item.uomAbbreviation || item.uomName || 'unit'}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3 text-center">
                                                    {wastePercent ? (
                                                        <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-md font-semibold">
                                                            <AlertTriangle size={10} /> {wastePercent}%
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-right text-sm font-semibold text-slate-700">
                                                    {itemCost}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {recipe.items.length > 8 && (
                            <div className="px-6 py-3 bg-slate-50/50 border-t border-slate-100 text-center">
                                <button onClick={() => setShowAllIngredients(!showAllIngredients)}
                                    className="flex items-center gap-1 mx-auto text-sm text-purple-600 font-medium hover:text-purple-700">
                                    {showAllIngredients ? 'Show Less' : `Show All ${recipe.items.length} Ingredients`}
                                    {showAllIngredients ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Production calculator */}
                <Card>
                    <CardHeader gradient className="bg-gradient-to-r from-indigo-50 to-blue-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-indigo-100"><Calculator className="w-5 h-5 text-indigo-600" /></div>
                            <div>
                                <h3 className="font-semibold text-slate-800">Production Calculator</h3>
                                <p className="text-xs text-slate-500">See exactly what's needed for any quantity</p>
                            </div>
                        </div>
                        <button onClick={() => setShowExplosion(!showExplosion)}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-xl text-sm font-semibold hover:bg-indigo-600">
                            <Calculator size={14} /> {showExplosion ? 'Hide' : 'Calculate'}
                        </button>
                    </CardHeader>
                    <AnimatePresence>
                        {showExplosion && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                                <CardContent className="p-6 space-y-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-semibold text-slate-600">Make</span>
                                        <div className="flex items-center border-2 border-slate-200 rounded-xl overflow-hidden">
                                            <button onClick={() => setExplosionQty(Math.max(1, explosionQty - 1))}
                                                className="px-3 py-2 hover:bg-slate-100 text-slate-500 font-bold">−</button>
                                            <input type="number" min="1" value={explosionQty}
                                                onChange={(e) => setExplosionQty(Math.max(1, parseInt(e.target.value) || 1))}
                                                className="w-20 px-2 py-2 text-center text-sm font-bold border-x border-slate-200 focus:outline-none" />
                                            <button onClick={() => setExplosionQty(explosionQty + 1)}
                                                className="px-3 py-2 hover:bg-slate-100 text-slate-500 font-bold">+</button>
                                        </div>
                                        <span className="text-sm text-slate-500">{recipe.yieldUomName || 'units'}</span>
                                    </div>

                                    {explosion && explosion.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-slate-100 text-sm">
                                                <thead>
                                                    <tr className="bg-slate-50">
                                                        <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Material</th>
                                                        <th className="px-4 py-2 text-right text-xs font-bold text-slate-500 uppercase">Needed (Base)</th>
                                                        <th className="px-4 py-2 text-right text-xs font-bold text-slate-500 uppercase">Cost</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-50">
                                                    {explosion.map((item: any, idx: number) => (
                                                        <tr key={idx}>
                                                            <td className="px-4 py-2 font-medium text-slate-700">{item.rawMaterialName || 'Unknown'}</td>
                                                            <td className="px-4 py-2 text-right font-bold text-indigo-600 tabular-nums">
                                                                {item.deductQuantity.toFixed(3)} {item.uomName || ''}
                                                            </td>
                                                            <td className="px-4 py-2 text-right font-semibold text-slate-700">
                                                                {item.cost ? formatNaira(item.cost) : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                {explosion.length > 0 && (
                                                    <tfoot>
                                                        <tr className="bg-slate-50">
                                                            <td className="px-4 py-2 font-bold text-slate-700">Total</td>
                                                            <td></td>
                                                            <td className="px-4 py-2 text-right font-bold text-slate-900">
                                                                {formatNaira(explosion.reduce((sum, i) => sum + (i.cost || 0), 0))}
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400 text-center py-4">No data. Ensure ingredients have units.</p>
                                    )}
                                </CardContent>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Card>

                {/* Business info */}
                <Card>
                    <CardContent className="p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <p className="text-xs text-slate-400 uppercase font-semibold">Created</p>
                                <p className="font-medium text-slate-700">{new Date(recipe.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase font-semibold">Last Updated</p>
                                <p className="font-medium text-slate-700">{new Date(recipe.updatedAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase font-semibold">Status</p>
                                <Badge variant={recipe.isActive ? 'success' : 'danger'} size="sm">{recipe.isActive ? 'Active' : 'Inactive'}</Badge>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 uppercase font-semibold">Total Ingredients</p>
                                <p className="font-bold text-slate-700">{recipe.items.length} items</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Deactivate modal */}
            <AnimatePresence>
                {confirmDelete && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setConfirmDelete(false)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 text-center" onClick={e => e.stopPropagation()}>
                            <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle size={32} className="text-rose-500" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900">Deactivate "{recipe.name}"?</h3>
                            <p className="text-sm text-slate-500 mt-2">It can be reactivated later from the recipes list.</p>
                            <div className="flex gap-3 mt-6">
                                <button onClick={() => setConfirmDelete(false)} className="flex-1 px-5 py-3 border-2 border-slate-200 rounded-xl text-sm font-semibold">Cancel</button>
                                <button onClick={() => deactivateMutation.mutate()} disabled={deactivateMutation.isPending}
                                    className="flex-1 px-5 py-3 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600 disabled:opacity-50">
                                    {deactivateMutation.isPending ? <Spinner size="xs" variant="white" /> : 'Deactivate'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function QuickStat({ icon: Icon, label, value, color, delay }: {
    icon: React.ElementType; label: string; value: string | number; color: string; delay: number;
}) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
            whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-lg p-4 hover:shadow-xl transition-all">
            <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${color} shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-lg font-black text-slate-900 mt-0.5 truncate">
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}